import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import { css } from "styled-components";
// Load all custom widgets so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const widgets = require.context("./Widgets", true, /\.tsx$/);
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept(widgets.id.toString(), () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
const pages = require.context("./Pages", true, /\.tsx$/);
const onHotPageReplace = addPagesFromContext(pages);
if (module.hot) {
    module.hot.accept(pages.id.toString(), () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
}

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// Load all the extensions to the base widgets. This includes style extensions.
const widgetExtensions = require.context("./WidgetExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));

setPreStyleGuideTheme({
    globalStyle: {
        css: css`
            li,
            a,
            nav,
            button,
            span {
                outline: none !important;
            }
            body {
                div[class^="GridOffset"],
                div[class*=" GridOffset"] {
                    margin-top: 0px;
                    margin-bottom: 0px;
                }

                //For shadow effect below header
                header {
                    z-index: 6;
                    position: relative;
                    overflow: visible !important;
                }

                header ~ div {
                    z-index: 1;
                    position: relative;
                }
            }
            main {
                //to span the page height on large screens with short content
                //this will give a small scroll to the pages with the page breadcrumbs
                min-height: 55.2vh;
            }
            input,
            textarea {
                & :disabled {
                    color: rgba(68, 68, 68, 0.75) !important;
                    background-color: ${UCSColors.gray09} !important;
                }
            }
            ::-webkit-scrollbar {
                height: 6px;
                width: 6px;
            }
            ::-webkit-scrollbar-track {
                background: ${UCSColors.gray09};
            }
            ::-webkit-scrollbar-thumb {
                background: ${UCSColors.gray03};
            }
            ::-webkit-scrollbar-thumb:hover {
                background: ${UCSColors.gray04};
            }
        `,
    },
    colors: {
        primary: {
            main: "#007A53",
        },
        secondary: {
            main: "#205C40",
            contrast: UCSColors.gray00,
        },
        text: {
            main: UCSColors.gray00,
            link: UCSColors.gray03,
            disabled: UCSColors.gray07,
            accent: UCSColors.gray10,
        },
        common: {
            background: "#E5E5E5",
            backgroundContrast: UCSColors.gray01,
        },
    },
    textField: {
        defaultProps: {
            backgroundColor: UCSColors.gray10,
        },
    },
    link: {
        defaultProps: {
            color: UCSColors.gray03,
        },
    },
    typography: {
        fontFamilyImportUrl: "https://use.typekit.net/idb1wkl.css",
    },
    breadcrumbs: {
        defaultProps: {
            css: css`
                color: ${UCSColors.gray10};
                font-size: 14px;
                font-weight: 400;
            `,
        },
    },
    button: {
        primary: {
            typographyProps: {
                weight: 500,
                transform: "uppercase",
            },
        },
        secondary: {
            color: "primary.main",
            hoverMode: "darken",
            typographyProps: {
                color: UCSColors.gray01,
                weight: 500,
                transform: "uppercase",
            },
            css: css`
                &:focus {
                    outline: none;
                }
                &:active {
                    outline: solid;
                    outline-width: thin;
                    outline-color: ${UCSColors.gray03};
                }
            `,
        },
    },
});

setPostStyleGuideTheme({
    link: {
        defaultProps: {
            css: css`
                &:active,
                &:focus {
                    border: none;
                    outline: none;
                }
            `,
        },
    },
    button: {
        defaultProps: {
            css: css`
                &:active,
                &:focus {
                    border: none;
                    outline: none;
                }
            `,
        },
    },
    typography: {
        body: {
            fontFamily: "'Interstate', sans-serif",
            weight: 400,
            lineHeight: "1.5em",
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 17px;
                        `,
                        css`
                            font-size: 17px;
                        `,
                        css`
                            font-size: 18px;
                        `,
                        css`
                            font-size: 18px;
                        `,
                        css`
                            font-size: 18px;
                        `,
                    ]);
                }}
            `,
        },
        h1: {
            weight: 400,
            css: css`
                margin: 1rem 0;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            letter-spacing: -2%;
                            font-size: 40px;
                            line-height: 40px;
                        `,
                        css`
                            letter-spacing: -2%;
                            font-size: 40px;
                            line-height: 48px;
                            line-height: 40px;
                        `,
                        css`
                            letter-spacing: -4%;
                            font-size: 48px;
                            line-height: 48px;
                        `,
                        css`
                            letter-spacing: -4%;
                            font-size: 48px;
                            line-height: 48px;
                        `,
                        css`
                            letter-spacing: -4%;
                            font-size: 48px;
                            line-height: 48px;
                        `,
                    ]);
                }}
            `,
        },
        h2: {
            weight: 400,
            css: css`
                margin: 1rem 0;
                letter-spacing: -2%;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 32px;
                            line-height: 32px;
                        `,
                        css`
                            font-size: 32px;
                            line-height: 32px;
                        `,
                        css`
                            font-size: 36px;
                            line-height: 36px;
                        `,
                        css`
                            font-size: 36px;
                            line-height: 36px;
                        `,
                        css`
                            font-size: 36px;
                            line-height: 36px;
                        `,
                    ]);
                }}
            `,
        },
        h3: {
            weight: 400,
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 28px;
                        `,
                        css`
                            font-size: 28px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                        css`
                            font-size: 30px;
                        `,
                    ]);
                }}
            `,
        },
        h4: {
            weight: 400,
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 22px;
                        `,
                        css`
                            font-size: 22px;
                        `,
                        css`
                            font-size: 24px;
                        `,
                        css`
                            font-size: 24px;
                        `,
                        css`
                            font-size: 24px;
                        `,
                    ]);
                }}
            `,
        },
        h5: {
            weight: 800,
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 12px;
                        `,
                        css`
                            font-size: 12px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                    ]);
                }}
            `,
        },
    },
});
